import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, MessagesSquare } from 'lucide-react';

import { AiChatDialogPanel } from '../components/AiChatDialogPanel';
import { MathTextBlock } from '../components/MathTextBlock';
import {
  getAiChatThread,
  type UserAiChatMessage,
  type UserAiChatThread,
} from '../services/userAiChatService';

function readContext(thread: UserAiChatThread) {
  const context = (thread.context || {}) as {
    surface?: string;
    ttqaId?: string;
    primaryParticleId?: string;
    selectedSegments?: Array<{ text?: string; particleId?: string }>;
    sourceParticles?: Array<{ particleId?: string; title?: string; questionText?: string }>;
    deepLink?: { path?: string; ttqaId?: string; particleId?: string; surface?: string };
  };
  return context;
}

export function LearningAiChatDetailPage() {
  const { threadId = '' } = useParams();
  const navigate = useNavigate();
  const [thread, setThread] = useState<UserAiChatThread | null>(null);
  const [messages, setMessages] = useState<UserAiChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [continueOpen, setContinueOpen] = useState(false);

  const load = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAiChatThread(threadId);
      setThread(data.thread);
      setMessages(data.messages);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    void load();
  }, [load]);

  const context = useMemo(() => (thread ? readContext(thread) : null), [thread]);

  const contextHref = useMemo(() => {
    if (!context) return null;
    const deep = context.deepLink;
    if (deep?.path) return deep.path;
    if (deep?.ttqaId || context.ttqaId) {
      return `/ttq/${encodeURIComponent(String(deep?.ttqaId || context.ttqaId))}`;
    }
    const particleId = deep?.particleId || context.primaryParticleId;
    if (particleId) {
      const returnTo = encodeURIComponent(`/learning/ai-chats/${threadId}`);
      return `/knowledge/${encodeURIComponent(particleId)}?from=ai-chat&returnTo=${returnTo}`;
    }
    return null;
  }, [context, threadId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-primary" />
      </div>
    );
  }

  if (!thread) {
    return <p className="text-danger">{error || '未找到对话'}</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Link to="/learning/ai-chats" className="inline-flex items-center gap-1 text-sm text-primary">
          <ArrowLeft size={14} />
          全部对话
        </Link>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-lg font-semibold text-ink">
            <MessagesSquare size={18} className="text-primary" />
            <span className="truncate">{thread.title || '未命名对话'}</span>
          </h1>
          <p className="mt-1 text-xs text-muted">
            更新于 {new Date(thread.updatedAt).toLocaleString()}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setContinueOpen(true)}
          className="shrink-0 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-white"
        >
          继续对话
        </button>
      </div>

      <section className="rounded-xl border border-border bg-surface p-4">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted">上下文</p>
        <div className="mt-2 space-y-2">
          {(context?.selectedSegments || []).map((item, index) => (
            <p key={`${item.particleId}-${index}`} className="text-sm text-ink">
              <span className="mr-1 text-primary">{index + 1}.</span>
              {item.text}
            </p>
          ))}
          {(context?.sourceParticles || []).slice(0, 2).map((particle) => (
            <p key={particle.particleId} className="line-clamp-3 text-xs text-muted">
              来源：{particle.title || particle.particleId} · {particle.questionText}
            </p>
          ))}
        </div>
        {contextHref ? (
          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary"
            onClick={() => navigate(contextHref)}
          >
            <ExternalLink size={12} />
            跳转到上下文
          </button>
        ) : (
          <p className="mt-3 text-xs text-muted">暂无可跳转定位</p>
        )}
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-surface p-4">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted">对话内容</p>
        {messages.length === 0 ? (
          <p className="text-sm text-muted">尚无消息</p>
        ) : null}
        {messages.map((item) => (
          <div
            key={item.id}
            className={
              item.role === 'user'
                ? 'ml-8 rounded-xl bg-primary/10 px-3 py-2 text-sm text-ink'
                : 'mr-8 rounded-xl border border-border bg-canvas/50 px-3 py-2 text-sm text-ink'
            }
          >
            {item.role === 'assistant' ? (
              item.content ? <MathTextBlock text={item.content} className="leading-relaxed" /> : null
            ) : (
              <p className="whitespace-pre-wrap leading-relaxed">{item.content}</p>
            )}
          </div>
        ))}
      </section>

      <AiChatDialogPanel
        open={continueOpen}
        onClose={() => {
          setContinueOpen(false);
          void load();
        }}
        particleId={context?.primaryParticleId}
        particleTitle={thread.title || undefined}
        contextBlocks={(context?.selectedSegments || []).map((item, index) => ({
          id: `ctx-${index}`,
          kind: 'text_segment' as const,
          particleId: item.particleId,
          text: String(item.text || ''),
        }))}
        entrypoint={thread.entrypoint || 'ttqib-segment'}
        surface={context?.surface || 'ttq_session'}
        ttqaId={context?.ttqaId}
        threadId={thread.id}
      />
    </div>
  );
}
